import { useState, useEffect } from "react";
import { Trash2, Check, AlignLeft, AlignCenter, AlignRight } from "lucide-react";
import FloatingBubble from "../FloatingBubble";
import ColorPopover from "../ColorPopover";
import "./Bubbles.css";

function parseStyle(el) {
  const s = el.style;
  return {
    bg: s.backgroundColor || s.background || "#6366f1",
    fg: s.color || "#ffffff",
    padding: s.padding || "12px 24px",
    radius: s.borderRadius || "8px",
    width: s.width || "",
  };
}

export default function ButtonBubble({ anchorEl, iframeEl, onClose, onChange }) {
  const [text, setText] = useState(anchorEl.textContent || "");
  const [url, setUrl] = useState(anchorEl.getAttribute("href") || "");
  const [newTab, setNewTab] = useState(anchorEl.getAttribute("target") === "_blank");
  const [style, setStyle] = useState(parseStyle(anchorEl));
  const [bgOpen, setBgOpen] = useState(false);
  const [fgOpen, setFgOpen] = useState(false);

  useEffect(() => {
    setText(anchorEl.textContent || "");
    setUrl(anchorEl.getAttribute("href") || "");
    setNewTab(anchorEl.getAttribute("target") === "_blank");
    setStyle(parseStyle(anchorEl));
  }, [anchorEl]);

  const liveUpdate = (patch) => {
    const next = { ...style, ...patch };
    setStyle(next);
    anchorEl.style.backgroundColor = next.bg;
    anchorEl.style.color = next.fg;
    anchorEl.style.padding = next.padding;
    anchorEl.style.borderRadius = next.radius;
    anchorEl.style.width = next.width;
    anchorEl.style.display = "inline-block";
    anchorEl.style.textDecoration = "none";
    onChange();
  };

  const apply = () => {
    anchorEl.textContent = text;
    anchorEl.setAttribute("href", url || "#");
    if (newTab) {
      anchorEl.setAttribute("target", "_blank");
      anchorEl.setAttribute("rel", "noopener noreferrer");
    } else {
      anchorEl.removeAttribute("target");
      anchorEl.removeAttribute("rel");
    }
    onChange();
    onClose();
  };

  const del = () => {
    const parent = anchorEl.parentNode;
    parent?.removeChild(anchorEl);
    onChange();
    onClose();
  };

  const setAlign = (a) => {
    const block = anchorEl.parentElement;
    if (block) block.style.textAlign = a;
    onChange();
  };

  return (
    <FloatingBubble anchorEl={anchorEl} iframeEl={iframeEl} label="Button" onClose={onClose}>
      <div className="bubble-field">
        <label>Text</label>
        <input type="text" value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      <div className="bubble-field">
        <label>URL</label>
        <input type="text" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." />
      </div>
      <label className="bubble-toggle">
        <input type="checkbox" checked={newTab} onChange={(e) => setNewTab(e.target.checked)} />
        Open in new tab
      </label>
      <div className="bubble-row">
        <div className="bubble-color-popover-wrap">
          <button type="button" className="bubble-color-trigger" onClick={() => setBgOpen((v) => !v)}>
            <span className="bubble-color-swatch" style={{ background: style.bg }} />
            BG
          </button>
          {bgOpen && <ColorPopover value={style.bg} onChange={(c) => liveUpdate({ bg: c })} onClose={() => setBgOpen(false)} />}
        </div>
        <div className="bubble-color-popover-wrap">
          <button type="button" className="bubble-color-trigger" onClick={() => setFgOpen((v) => !v)}>
            <span className="bubble-color-swatch" style={{ background: style.fg }} />
            Text
          </button>
          {fgOpen && <ColorPopover value={style.fg} onChange={(c) => liveUpdate({ fg: c })} onClose={() => setFgOpen(false)} />}
        </div>
      </div>
      <div className="bubble-row">
        <div className="bubble-field" style={{ flex: 1 }}>
          <label>Padding</label>
          <input type="text" value={style.padding} onChange={(e) => liveUpdate({ padding: e.target.value })} placeholder="12px 24px" />
        </div>
        <div className="bubble-field" style={{ flex: 1 }}>
          <label>Radius</label>
          <input type="text" value={style.radius} onChange={(e) => liveUpdate({ radius: e.target.value })} placeholder="8px" />
        </div>
      </div>
      <div className="bubble-row">
        <div className="bubble-field" style={{ flex: 1 }}>
          <label>Width</label>
          <select
            value={style.width === "100%" ? "full" : "auto"}
            onChange={(e) => liveUpdate({ width: e.target.value === "full" ? "100%" : "" })}
            style={{ height: 30, border: "1px solid #e2e8f0", borderRadius: 6, padding: "0 8px" }}
          >
            <option value="auto">Auto</option>
            <option value="full">Full width</option>
          </select>
        </div>
        <div className="bubble-row" style={{ gap: 4 }}>
          <button type="button" className="bubble-btn icon-only" title="Align left" onClick={() => setAlign("left")}><AlignLeft size={13} /></button>
          <button type="button" className="bubble-btn icon-only" title="Align center" onClick={() => setAlign("center")}><AlignCenter size={13} /></button>
          <button type="button" className="bubble-btn icon-only" title="Align right" onClick={() => setAlign("right")}><AlignRight size={13} /></button>
        </div>
      </div>
      <div className="bubble-actions">
        <button type="button" className="bubble-btn primary" onClick={apply}><Check size={12} /> Apply</button>
        <button type="button" className="bubble-btn danger" onClick={del}><Trash2 size={12} /> Delete</button>
      </div>
    </FloatingBubble>
  );
}
